import { useCallback, useEffect, useRef, useState } from 'react'
import type { ApolloError } from '@truepill/tpos-react-router'
import { useApolloClient, useLocation, useMutation, useQuery } from '@truepill/tpos-react-router'
import { BULK_ACTION_HISTORY_PAGE_SIZE, bulkActionRoles, LaunchDarkly } from '@truepill/tpos-types'
import type { BulkActionSourceQueue, CreateBulkActionStepArgs } from '@truepill/tpos-types'
import { ABORT_BULK_ACTION, CREATE_BULK_ACTION, GET_BULK_ACTIONS } from 'gql'
import { useUserInfo } from 'hooks'
import useQueryParamString from 'hooks/useQueryParamString'
import { useClient, useFlag } from 'providers/LaunchDarklyProvider'
import { usePlusClient } from 'providers/VisionRouter'
import type { BulkAction, BulkActionHistoryPage } from 'types'
import { isBulkActionTerminal } from 'utils/bulkActions'
import { getQueueNameFromPath } from 'utils/fulfillmentQueue'
import { getLocationIdFromQueryAndContext } from 'utils/location'

const BULK_ACTION_POLLING_CEILING_MS = 300_000

const compareByCreatedAtDesc = (a: BulkAction, b: BulkAction): number => {
  const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
  const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
  if (aTime !== bTime) return bTime - aTime
  const aId = a._id.toString()
  const bId = b._id.toString()
  return aId < bId ? 1 : aId > bId ? -1 : 0
}

// Merge a page into the list by _id (update existing, insert new, never drop), newest first.
const upsertById = (existing: BulkAction[], incoming: BulkAction[]): BulkAction[] => {
  if (incoming.length === 0) return existing
  const byId = new Map(existing.map(ba => [ba._id.toString(), ba]))
  for (const ba of incoming) byId.set(ba._id.toString(), ba)
  return [...byId.values()].sort(compareByCreatedAtDesc)
}

export const useBulkActionAccess = (): boolean => {
  const {
    tokenContext: { id: userId, hasRole },
  } = usePlusClient()
  const { ldReady, ldIdentify } = useClient()

  const { userInfo } = useUserInfo(userId)
  const userEmail = userInfo?.email

  useEffect(() => {
    if (!ldReady || !userId || !userEmail) return
    ldIdentify({ key: userId, custom: { email: userEmail } })
  }, [ldReady, ldIdentify, userId, userEmail])

  const isBulkActionsEnabled = !!useFlag(LaunchDarkly.FeatureFlags.ENABLE_BULK_ACTIONS)
  const hasAllowedRole = hasRole ? hasRole(bulkActionRoles) : false

  return isBulkActionsEnabled && hasAllowedRole
}

type UseBulkActionsHistoryResult = {
  bulkActions: BulkAction[]
  loading: boolean
  loadingMore: boolean
  error?: ApolloError
  locationId?: string
  startPolling: (interval: number) => void
  loadMore: () => void
  hasMore: boolean
}

// Detects bulk actions transitioning from incomplete -> complete and refreshes the
// current queue's orders if the action was started from that queue; Use same URL params
// except for the page so the user goes back to page 0 to avoid empty pages scenarios.
export const useRefetchSourceQueueOnCompletion = (bulkActions: BulkAction[]): void => {
  const client = useApolloClient()
  const { pathname } = useLocation()
  const currentQueueName = getQueueNameFromPath(pathname)
  const [page, setPage] = useQueryParamString('page')
  const previouslyIncompleteActionsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const justCompleted = bulkActions.filter(
      ba => previouslyIncompleteActionsRef.current.has(ba._id.toString()) && isBulkActionTerminal(ba),
    )
    const matchesCurrentQueue = justCompleted.some(ba => currentQueueName === ba.sourceQueue)

    if (matchesCurrentQueue) {
      if (page) {
        setPage(undefined)
      } else {
        client.refetchQueries({ include: ['getFulfillmentOrders'] })
      }
    }

    previouslyIncompleteActionsRef.current = new Set(
      bulkActions.filter(ba => !isBulkActionTerminal(ba)).map(ba => ba._id.toString()),
    )
  }, [bulkActions, currentQueueName, client, page, setPage])
}

export const useBulkActionsHistory = (): UseBulkActionsHistoryResult => {
  const {
    tokenContext,
    currentLocation: { queryMap },
  } = usePlusClient()
  const { locationId } = getLocationIdFromQueryAndContext(queryMap, tokenContext)
  const currentUserId = tokenContext.id
  const canAccessBulkActions = useBulkActionAccess()
  const client = useApolloClient()

  // Page 1 is the live head: polled for progress and the target of the create-time prepend.
  const { data, loading, error, startPolling, stopPolling } = useQuery<
    { getBulkActions: BulkActionHistoryPage },
    { locationId?: string; limit: number }
  >(GET_BULK_ACTIONS, {
    skip: !canAccessBulkActions || !locationId,
    variables: { locationId, limit: BULK_ACTION_HISTORY_PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const firstPage = data?.getBulkActions

  const [bulkActions, setBulkActions] = useState<BulkAction[]>([])
  const [tailPaging, setTailPaging] = useState<{ cursor: string | null; hasMore: boolean } | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)

  const nextCursor = tailPaging ? tailPaging.cursor : firstPage?.nextCursor ?? null
  const hasMore = tailPaging ? tailPaging.hasMore : firstPage?.hasMore ?? false

  // A location change is a different shared log — reset the accumulator and frontier.
  useEffect(() => {
    setBulkActions([])
    setTailPaging(null)
  }, [locationId])

  // Fold every page-1 result (initial load, each poll, create prepend) into the accumulator.
  useEffect(() => {
    if (firstPage) setBulkActions(prev => upsertById(prev, firstPage.bulkActions))
  }, [firstPage])

  const loadMore = useCallback(async () => {
    if (!locationId || !nextCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const { data: moreData } = await client.query<
        { getBulkActions: BulkActionHistoryPage },
        { locationId: string; limit: number; cursor: string }
      >({
        query: GET_BULK_ACTIONS,
        variables: { locationId, limit: BULK_ACTION_HISTORY_PAGE_SIZE, cursor: nextCursor },
        fetchPolicy: 'no-cache',
      })
      const page = moreData?.getBulkActions
      if (!page) return
      setBulkActions(prev => upsertById(prev, page.bulkActions))
      setTailPaging({ cursor: page.nextCursor ?? null, hasMore: page.hasMore })
    } catch {
      // Keep the frontier so the user can retry by clicking again.
    } finally {
      setLoadingMore(false)
    }
  }, [client, locationId, nextCursor, loadingMore])

  // Stop polling once the current user's most recent created action (always on the head) is complete.
  useEffect(() => {
    const headActions = firstPage?.bulkActions ?? []
    if (headActions.length === 0) return
    const userLatest = headActions.find(ba => String(ba.userId) === currentUserId)
    if (!userLatest || isBulkActionTerminal(userLatest)) return stopPolling()

    const olderThanCeiling = userLatest.createdAt ? Date.now() - new Date(userLatest.createdAt).getTime() : 0
    if (olderThanCeiling > BULK_ACTION_POLLING_CEILING_MS) stopPolling()
  }, [firstPage, currentUserId, stopPolling])

  return { bulkActions, loading, loadingMore, error, locationId, startPolling, loadMore, hasMore }
}

type CreateBulkActionVars = {
  orderIds: string[]
  steps: CreateBulkActionStepArgs[]
  locationId: string
  sourceQueue: BulkActionSourceQueue
}

type UseCreateBulkActionResult = {
  createBulkAction: (vars: CreateBulkActionVars) => Promise<BulkAction | undefined>
  loading: boolean
}

export const useCreateBulkAction = (): UseCreateBulkActionResult => {
  const {
    tokenContext,
    currentLocation: { queryMap },
  } = usePlusClient()
  const { locationId: historyLocationId } = getLocationIdFromQueryAndContext(queryMap, tokenContext)

  const [mutate, { loading }] = useMutation<{ createBulkAction: BulkAction }, CreateBulkActionVars>(
    CREATE_BULK_ACTION,
    {
      update(cache, { data }) {
        const created = data?.createBulkAction
        if (!created || !historyLocationId || created.locationId?.toString() !== historyLocationId.toString()) return

        cache.updateQuery<{ getBulkActions: BulkActionHistoryPage }, { locationId: string; limit: number }>(
          {
            query: GET_BULK_ACTIONS,
            variables: { locationId: historyLocationId, limit: BULK_ACTION_HISTORY_PAGE_SIZE },
          },
          prev => {
            const page = prev?.getBulkActions
            if (!page) return prev
            const withoutCreated = page.bulkActions.filter(ba => ba._id.toString() !== created._id.toString())
            return { getBulkActions: { ...page, bulkActions: [created, ...withoutCreated] } }
          },
        )
      },
    },
  )

  const createBulkAction = async (vars: CreateBulkActionVars) => {
    const result = await mutate({ variables: vars })
    return result.data?.createBulkAction
  }

  return { createBulkAction, loading }
}

type UseAbortBulkActionResult = {
  abortBulkAction: (bulkActionId: string) => Promise<BulkAction | undefined>
  loading: boolean
}

// The mutation returns the updated BulkAction (with abortedAt). Apollo normalizes it
// by _id into the existing GET_BULK_ACTIONS cache entry, so the History card flips to terminal
// immediately — no cache surgery and no reliance on polling (which matters when aborting an
// older/zombie job for which polling was never started).
export const useAbortBulkAction = (): UseAbortBulkActionResult => {
  const [mutate, { loading }] = useMutation<{ abortBulkAction: BulkAction }, { bulkActionId: string }>(
    ABORT_BULK_ACTION,
  )

  const abortBulkAction = async (bulkActionId: string) => {
    const result = await mutate({ variables: { bulkActionId } })
    return result.data?.abortBulkAction
  }

  return { abortBulkAction, loading }
}
